import { defineComponent as _defineComponent } from 'vue'
import {useTranslations} from "@/hooks/useTranslations";
import SignupDto from "@/models/Restaurants/SignupDto";
import {computed, getCurrentInstance, onMounted, ref} from "vue";
import {useNotify} from "@/hooks/useNotify";
import CustomersRepository from "@/utils/API/CustomersRepository";
import {getMarketingParams} from "@/utils/socialAuth";
import Industry from "@/models/Industry";
import Country from "@/models/Country";
import GeneralRepository from "@/utils/API/GeneralRepository";
import SubscriptionRepository from "@/utils/API/SubscriptionRepository";


export default /*#__PURE__*/_defineComponent({
  __name: 'FreeTrialSignupForm',
  emits: ["googleSignupRequested"],
  setup(__props, { emit }) {



const translations = useTranslations();
const { proxy } = getCurrentInstance();
const validator = proxy.$validator;
const notify = useNotify();
const emailPasswordOption = "emailPassword";
const socialOption = "social";

const signupFormData = ref<Omit<SignupDto, "marketingParams" | "shouldSkipFreeTrial" | "countryId" | "industryId">>({
  name: "",
  phone: "",
  ownerFirstName: "",
  ownerLastName: "",
  ownerEmail: "",
  ownerPassword: "",
  confirmOwnerPassword: "",
  referrer: "",
  hasRequestedNewsLetter: false,
});
const phoneTouched = ref(false);
const invalidPhone = ref(false);
const agreeTerms = ref(false);
const passwordMinLength = ref(8);
const countryList = ref<Country[]>([]);
const industryList = ref<Industry[]>([]);
const selectedSignInMethod = ref(emailPasswordOption);

const selectedIndustry = ref<Industry>();
const selectedCountry = ref<Country>();

const freeTrialDays = ref<number>(0);

onMounted(async() => {
  await getTrialDays();
  countryList.value = (await GeneralRepository.listCountries()).data.result;
  industryList.value = (await GeneralRepository.listIndustries()).data.result;
});

async function getTrialDays() {
  try {
    const response = await SubscriptionRepository.getSubscriptionSystemSettings();
    freeTrialDays.value = response.data.result.trialDays;
  }

  catch (error: any) {
    notify.error(
      error.response
        ? error.response.errorMessages[0].userVisibleMessage
        : translations.value.get("network-error")
    );
  }
}

async function createAccount() {
  phoneTouched.value = true;

  const result = await validator.validateAll("accCreate");

  if (result && !invalidPhone.value) {
    if (!agreeTerms.value) {
      notify.error(translations.value.get("login_accept_terms_message"));
      return;
    }

    notify.info(translations.value.get("login_creating"));

    try {
      await signup();
      window.location = process.env.VUE_APP_THANKYOUPAGE_URL;
    }

    catch (error: any) {
      const message = error.response
        ? error.response.data.errorMessages[0].userVisibleMessage
        : translations.value.get("network_error");

      notify.error(message);
    }

    return;
  }

  notify.error(translations.value.get("validation-error-message"));
}

function startGoogleSignup() {
  phoneTouched.value = true;

  if (!signupFormData.value.name?.trim()) {
    notify.error("Restaurant name is required.");
    return;
  }

  if (!signupFormData.value.phone?.trim() || invalidPhone.value) {
    notify.error("Please enter a valid restaurant phone number.");
    return;
  }

  if (!selectedCountry.value?.id) {
    notify.error("Country is required.");
    return;
  }

  if (!selectedIndustry.value?.id) {
    notify.error("Industry is required.");
    return;
  }

  if (!signupFormData.value.referrer) {
    notify.error("Please tell us how you heard about MenuSano.");
    return;
  }

  if (!agreeTerms.value) {
    notify.error(translations.value.get("login_accept_terms_message"));
    return;
  }

  emit("googleSignupRequested", {
    shouldSkipFreeTrial: false,
    countryId: selectedCountry.value.id,
    industryId: selectedIndustry.value.id,
    marketingParams: getMarketingParams(),
    ...signupFormData.value,
    ownerPassword: "",
    confirmOwnerPassword: "",
  });
}

async function signup() {
  await CustomersRepository.signup({
    shouldSkipFreeTrial: false,
    countryId: selectedCountry.value.id,
    industryId: selectedIndustry.value.id,
    marketingParams: getMarketingParams(),
    ...signupFormData.value,
  });
}

function handlePhoneValidation(response: any){
  invalidPhone.value = !response.isValid;
}

const isEmailPasswordSelected = computed(() => selectedSignInMethod.value === emailPasswordOption);
const emailValidationRule = computed(() => isEmailPasswordSelected.value ? "required|email" : "email");

return { __sfc: true,emit, translations, proxy, validator, notify, emailPasswordOption, socialOption, signupFormData, phoneTouched, invalidPhone, agreeTerms, passwordMinLength, countryList, industryList, selectedSignInMethod, selectedIndustry, selectedCountry, freeTrialDays, getTrialDays, createAccount, startGoogleSignup, signup, handlePhoneValidation, isEmailPasswordSelected, emailValidationRule }
}

})